// src/pages/HostDashboard.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/authContext';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../utils/firebase';
import AdminRevenueDashboard from '../components/dashboard/AdminRevenueDashboard';
import RevenueTracker from '../components/RevenueTracker';
import StripeConnectSetup from '../components/StripeConnectSetup';
import StripeConnectPrompt from '../components/StripeConnectPrompt';
import HostLayout from '../layouts/HostLayout';

const HostDashboard = () => {
  const navigate = useNavigate();
  const { ready, claims, user } = useAuth();
  const [userData, setUserData] = useState(null);
  const [showStripePrompt, setShowStripePrompt] = useState(true);

  useEffect(() => {
    if (!user) return;
    
    const loadUserData = async () => {
      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          setUserData(userDoc.data());
        }
      } catch (error) {
        console.error('Error loading user data:', error);
      }
    };
    
    loadUserData();
  }, [user]);

  if (!ready) return <div className="p-6 text-sm opacity-70">Loading…</div>;

  // Admin: aggregated revenue via admin callable (no client reads of revenue_events).
  if (claims?.admin) {
    return <AdminRevenueDashboard />;
  }

  return (
    <HostLayout>
      <div className="min-h-screen bg-gray-900">
        {/* Page Header */}
        <div className="bg-gray-800 border-b border-gray-700 p-6">
          <div className="max-w-7xl mx-auto">
            <div>
              <h1 className="text-3xl font-bold text-white">Host Dashboard</h1>
              <p className="text-gray-400 mt-2">Build games, launch shows, and earn from the marketplace</p>
            </div>
          </div>
        </div>

                <div className="max-w-7xl mx-auto p-6">
                {/* Stripe Connect Setup Prompt */}
                {showStripePrompt && userData && (
                  <StripeConnectPrompt 
                    userData={userData} 
                    onDismiss={() => setShowStripePrompt(false)} 
                  />
                )}
                
                {/* Pricing Overview */}
        <div className="bg-gray-800 rounded-lg p-6 mb-8">
          <h2 className="text-2xl font-bold text-white mb-4">Simple Pricing</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-gray-700 rounded-lg p-4">
              <div className="text-3xl font-bold text-white mb-2">Free</div>
              <div className="text-gray-400 text-sm mb-2">Game Creation</div>
              <div className="text-gray-300 text-sm">
                Build unlimited games and save them to your profile
              </div>
            </div>

            <div className="bg-gray-700 rounded-lg p-4">
              <div className="text-3xl font-bold text-white mb-2">$15</div>
              <div className="text-gray-400 text-sm mb-2">Per Launch</div>
              <div className="text-gray-300 text-sm">
                Launch your created games with 12-hour window
              </div>
            </div>

            <div className="bg-gray-700 rounded-lg p-4">
              <div className="text-3xl font-bold text-white mb-2">50%</div>
              <div className="text-gray-400 text-sm mb-2">Revenue Split</div>
              <div className="text-gray-300 text-sm">
                Set your price and keep 50% of all sales
              </div>
            </div>
          </div>
        </div>

        {/* Stripe Connect Setup */}
        <div className="mb-8">
          <StripeConnectSetup />
        </div>

        {/* How It Works */}
        <div className="bg-gray-800 rounded-lg p-6 mb-8">
          <h2 className="text-2xl font-bold text-white mb-6">How It Works</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <button 
              onClick={() => navigate('/add-game')}
              className="text-center w-full hover:scale-105 transition-transform cursor-pointer"
            >
              <div className="bg-blue-600 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4 hover:bg-blue-700 transition-colors">
                <span className="text-2xl">🎮</span>
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Build Games</h3>
              <p className="text-gray-300 text-sm">
                Create unlimited trivia games for free. Save them to your profile and preview them anytime.
              </p>
            </button>

            <button 
              onClick={() => navigate('/my-games')}
              className="text-center w-full hover:scale-105 transition-transform cursor-pointer"
            >
              <div className="bg-green-600 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4 hover:bg-green-700 transition-colors">
                <span className="text-2xl">🚀</span>
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Launch Shows</h3>
              <p className="text-gray-300 text-sm">
                Pay $15 per launch to host your games live. Each launch includes a 12-hour window.
              </p>
            </button>

            <div className="text-center">
              <div className="bg-purple-600 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">💰</span>
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Earn Money</h3>
              <p className="text-gray-300 text-sm">
                Set your game price and submit for marketplace approval. Approved games offer a 50% revenue split. You keep 50% of game price.
              </p>
            </div>
          </div>
        </div>

        {/* Revenue Tracker */}
        <RevenueTracker />

        {/* Quick Actions */}
        <div className="bg-gray-800 rounded-lg p-6">
          <h2 className="text-2xl font-bold text-white mb-4">Quick Actions</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <button
              onClick={() => navigate('/add-game')}
              className="bg-blue-600 text-white p-4 rounded-lg hover:bg-blue-700 transition-colors text-center"
            >
              <div className="text-2xl mb-2">🎮</div>
              <div className="font-semibold">Create Game</div>
              <div className="text-sm text-blue-200">Build a new trivia game</div>
            </button>

            <button
              onClick={() => navigate('/my-games')}
              className="bg-gray-600 text-white p-4 rounded-lg hover:bg-gray-500 transition-colors text-center"
            >
              <div className="text-2xl mb-2">📋</div>
              <div className="font-semibold">My Games</div>
              <div className="text-sm text-gray-300">Manage your games</div>
            </button>

            <button
              onClick={() => navigate('/marketplace')}
              className="bg-green-600 text-white p-4 rounded-lg hover:bg-green-700 transition-colors text-center"
            >
              <div className="text-2xl mb-2">🏪</div>
              <div className="font-semibold">Marketplace</div>
              <div className="text-sm text-green-200">Browse games</div>
            </button>

            <button
              onClick={() => navigate('/account-settings')}
              className="bg-purple-600 text-white p-4 rounded-lg hover:bg-purple-700 transition-colors text-center"
            >
              <div className="text-2xl mb-2">⚙️</div>
              <div className="font-semibold">Settings</div>
              <div className="text-sm text-purple-200">Manage account</div>
            </button>
          </div>
        </div>
        </div>
      </div>
    </HostLayout>
  );
};

export default HostDashboard;